/**
 * Fournisseurs d'authentification — module UNIQUE.
 *
 * Sécurité (voir SECURITY.md) :
 *  - Aucun secret côté client. Discord passe par le proxy (OAuth 2 + PKCE + state, jeton
 *    personnalisé Firebase remis via un code à usage unique — jamais dans une URL durable).
 *  - JAMAIS de liaison automatique de comptes par e-mail (prise de contrôle de compte) : si un
 *    e-mail existe déjà avec un autre fournisseur, on demande à l'utilisateur de se connecter avec
 *    ce fournisseur, puis de lier le nouveau depuis son profil (état connecté requis).
 *  - Mots de passe : 8 caractères minimum, lettres + chiffres, et vérification k-anonyme contre
 *    les fuites connues (HaveIBeenPwned range API : seuls 5 caractères du SHA-1 sont envoyés).
 *  - Mode invité (anonyme) : progression sauvegardée, mais pas d'accès au prof IA tant que le
 *    compte n'est pas rattaché à un fournisseur (décision serveur).
 *
 * Natif (Capacitor) : le plugin @capacitor-firebase/authentication (skipNativeAuth) fournit le
 * credential, puis le SDK JS se connecte (signInWithCredential) ou lie (linkWithCredential).
 * Web : signInWithPopup / linkWithPopup.
 */
import {
  AuthCredential,
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  User,
  deleteUser,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  linkWithRedirect,
  getRedirectResult,
  updateEmail,
  sendEmailVerification,
} from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Capacitor } from "@capacitor/core";
import { ref, remove } from "firebase/database";
import { auth, db, usingEmulators } from "../firebase";
import { track } from "./analytics";
import { discordExchange, discordStart, deleteAccountOnServer } from "./proxyApi";

export type ProviderId = "password" | "emailLink" | "google" | "apple" | "microsoft" | "facebook" | "discord" | "phone" | "anonymous";
export type OAuthProviderId = "google" | "apple" | "microsoft" | "facebook";

const DEFAULT_PROVIDERS: ProviderId[] = ["password", "emailLink", "google", "apple", "microsoft", "discord", "anonymous"];

/** Fournisseurs activés (REACT_APP_AUTH_PROVIDERS="google,apple,..."). Apple : iOS toujours, web si activé. */
export function enabledProviders(): ProviderId[] {
  const raw = process.env.REACT_APP_AUTH_PROVIDERS;
  const list = (raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : DEFAULT_PROVIDERS) as ProviderId[];
  const platform = Capacitor.getPlatform();
  return list.filter((p) => {
    if (p === "apple" && platform === "android") return false;
    return true;
  });
}

export const isNative = () => Capacitor.isNativePlatform();

/** Navigateur mobile (Safari iOS, Chrome Android…) : les popups y sont bloquées ou fermées
 *  d'office (« Connexion annulée »). On utilise alors la redirection, possible grâce au relais
 *  /__/auth sur notre domaine (authDomain = scholeo.ch). */
export const isMobileBrowser = () => !isNative() && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const REDIRECT_PENDING_KEY = "st_auth_redirect_pending";

/** À appeler une fois au chargement de l'écran de connexion : termine une connexion par
 *  redirection (mobile). Renvoie true si un utilisateur vient d'être connecté, lève l'erreur Firebase sinon. */
export async function completeRedirectSignIn(): Promise<boolean> {
  let pending: string | null = null;
  try {
    pending = sessionStorage.getItem(REDIRECT_PENDING_KEY);
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
  } catch (e) {
    /* stockage indisponible */
  }
  if (!pending) return false;
  const result = await getRedirectResult(auth);
  if (result && result.user) {
    track(pending.startsWith("link:") ? "account_linked" : "login_completed", { method: pending.replace(/^(link|login):/, "") });
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Fournisseurs OAuth (Google, Apple, Microsoft, Facebook)
// ---------------------------------------------------------------------------
function webProvider(id: OAuthProviderId) {
  switch (id) {
    case "google": {
      const p = new GoogleAuthProvider();
      p.setCustomParameters({ prompt: "select_account" });
      return p;
    }
    case "apple": {
      const p = new OAuthProvider("apple.com");
      p.addScope("email");
      p.addScope("name");
      return p;
    }
    case "microsoft": {
      const p = new OAuthProvider("microsoft.com");
      // "common" = comptes personnels ET comptes scolaires / professionnels (Microsoft 365 des écoles)
      p.setCustomParameters({ prompt: "select_account", tenant: "common" });
      return p;
    }
    case "facebook": {
      const p = new FacebookAuthProvider();
      p.addScope("email");
      return p;
    }
  }
}

/** Obtient un credential Firebase via le plugin natif (le SDK JS n'est PAS encore connecté). */
async function nativeCredential(id: OAuthProviderId): Promise<AuthCredential> {
  switch (id) {
    case "google": {
      const r = await FirebaseAuthentication.signInWithGoogle();
      return GoogleAuthProvider.credential(r.credential?.idToken, r.credential?.accessToken);
    }
    case "apple": {
      const r = await FirebaseAuthentication.signInWithApple({ scopes: ["email", "name"] });
      return new OAuthProvider("apple.com").credential({ idToken: r.credential?.idToken, rawNonce: r.credential?.nonce });
    }
    case "microsoft": {
      const r = await FirebaseAuthentication.signInWithMicrosoft({ customParameters: [{ key: "tenant", value: "common" }, { key: "prompt", value: "select_account" }] });
      return new OAuthProvider("microsoft.com").credential({ idToken: r.credential?.idToken, accessToken: r.credential?.accessToken, rawNonce: r.credential?.nonce });
    }
    case "facebook": {
      const r = await FirebaseAuthentication.signInWithFacebook();
      return FacebookAuthProvider.credential(r.credential?.accessToken || "");
    }
  }
}

export async function signInWithProvider(id: OAuthProviderId): Promise<void> {
  if (isNative()) {
    await signInWithCredential(auth, await nativeCredential(id));
  } else if (isMobileBrowser()) {
    try {
      sessionStorage.setItem(REDIRECT_PENDING_KEY, `login:${id}`);
    } catch (e) {
      /* ignore */
    }
    await signInWithRedirect(auth, webProvider(id)); // la page quitte l'app ; suite dans completeRedirectSignIn
    return;
  } else {
    await signInWithPopup(auth, webProvider(id));
  }
  track("login_completed", { method: id });
}

/** Lie un fournisseur au compte courant (invité → compte réel, ou second fournisseur). */
export async function linkProvider(id: OAuthProviderId): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  if (isNative()) {
    await linkWithCredential(user, await nativeCredential(id));
  } else if (isMobileBrowser()) {
    try {
      sessionStorage.setItem(REDIRECT_PENDING_KEY, `link:${id}`);
    } catch (e) {
      /* ignore */
    }
    await linkWithRedirect(user, webProvider(id));
    return;
  } else {
    await linkWithPopup(user, webProvider(id));
  }
  track("account_linked", { method: id });
}

// ---------------------------------------------------------------------------
// E-mail + mot de passe (politique de mot de passe)
// ---------------------------------------------------------------------------
export function passwordIssues(pw: string): string[] {
  const issues: string[] = [];
  if (pw.length < 8) issues.push("auth.pw.tooShort");
  if (!/[a-zA-Z]/.test(pw)) issues.push("auth.pw.needLetter");
  if (!/\d/.test(pw)) issues.push("auth.pw.needDigit");
  if (/^(.)\1+$/.test(pw) || /^(0123|1234|abcd|azerty|qwerty|password|motdepasse)/i.test(pw)) issues.push("auth.pw.tooCommon");
  return issues;
}

/** Vérification k-anonyme HaveIBeenPwned (5 premiers caractères du SHA-1 seulement). Fail-open. */
export async function isPasswordPwned(pw: string): Promise<boolean> {
  try {
    if (!window.crypto?.subtle) return false;
    const buf = await window.crypto.subtle.digest("SHA-1", new TextEncoder().encode(pw));
    const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
    const prefix = hex.slice(0, 5);
    const suffix = hex.slice(5);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3500);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, { headers: { "Add-Padding": "true" }, signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return false;
    const text = await res.text();
    return text.split("\n").some((line) => line.split(":")[0].trim() === suffix);
  } catch (e) {
    return false;
  }
}

/** Invité → compte e-mail + mot de passe (garde la progression). */
export async function upgradeGuestWithEmail(email: string, password: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  const cred = EmailAuthProvider.credential(email.trim(), password);
  await linkWithCredential(user, cred);
  if (!usingEmulators) await sendEmailVerification(user);
  track("account_linked", { method: "password" });
}

/** Ajoute un e-mail (+ vérification) à un compte sans e-mail (téléphone, Discord sans e-mail). */
export async function setAccountEmail(email: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  await updateEmail(user, email.trim());
  if (!usingEmulators) await sendEmailVerification(user);
}

// ---------------------------------------------------------------------------
// Lien magique (connexion sans mot de passe)
// ---------------------------------------------------------------------------
const EMAIL_LINK_KEY = "st_email_for_link";
export const EMAIL_LINK_PATH = "/auth/email-link";

export async function sendMagicLink(email: string): Promise<void> {
  const url = `${window.location.origin}${EMAIL_LINK_PATH}`;
  await sendSignInLinkToEmail(auth, email.trim(), { url, handleCodeInApp: true });
  try {
    localStorage.setItem(EMAIL_LINK_KEY, email.trim());
  } catch (e) {
    /* stockage indisponible */
  }
}

export function isMagicLink(url: string): boolean {
  return isSignInWithEmailLink(auth, url);
}

/** Complète la connexion par lien. Renvoie false si l'e-mail doit être demandé (autre appareil). */
export async function completeMagicLink(url: string, emailOverride?: string): Promise<boolean> {
  let email = emailOverride || "";
  if (!email) {
    try {
      email = localStorage.getItem(EMAIL_LINK_KEY) || "";
    } catch (e) {
      /* ignore */
    }
  }
  if (!email) return false;
  const user = auth.currentUser;
  if (user && user.isAnonymous) {
    await linkWithCredential(user, EmailAuthProvider.credentialWithLink(email, url));
    track("account_linked", { method: "emailLink" });
  } else {
    await signInWithEmailLink(auth, email, url);
    track("login_completed", { method: "emailLink" });
  }
  try {
    localStorage.removeItem(EMAIL_LINK_KEY);
  } catch (e) {
    /* ignore */
  }
  return true;
}

// ---------------------------------------------------------------------------
// Discord (via le proxy : OAuth + PKCE, code à usage unique → jeton personnalisé)
// ---------------------------------------------------------------------------
export async function startDiscordSignIn(mode: "login" | "link"): Promise<void> {
  const platform = isNative() ? "native" : "web";
  const { url } = await discordStart({ platform, link: mode === "link" });
  if (isNative()) {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url, presentationStyle: "popover" });
  } else {
    window.location.href = url;
  }
}

export async function completeDiscordSignIn(code: string): Promise<{ linked: boolean }> {
  const { token, linked } = await discordExchange(code);
  await signInWithCustomToken(auth, token);
  track(linked ? "account_linked" : "login_completed", { method: "discord" });
  return { linked };
}

// ---------------------------------------------------------------------------
// Téléphone
// ---------------------------------------------------------------------------
export interface PhoneSession {
  verificationId: string;
}

/** Web : `verifier` = RecaptchaVerifier ; natif : plugin (événement phoneCodeSent). */
export async function startPhoneSignIn(phoneNumber: string, webVerifier?: any): Promise<PhoneSession> {
  if (isNative()) {
    return new Promise<PhoneSession>((resolve, reject) => {
      let done = false;
      const sentHandle = FirebaseAuthentication.addListener("phoneCodeSent", (ev) => {
        if (done) return;
        done = true;
        resolve({ verificationId: ev.verificationId });
      });
      const failHandle = FirebaseAuthentication.addListener("phoneVerificationFailed", (ev) => {
        if (done) return;
        done = true;
        reject(new Error(ev.message || "phone_failed"));
      });
      FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber }).catch((e) => {
        if (!done) {
          done = true;
          reject(e);
        }
      });
      setTimeout(() => {
        sentHandle.then((h) => h.remove());
        failHandle.then((h) => h.remove());
      }, 120000);
    });
  }
  const provider = new PhoneAuthProvider(auth);
  const verificationId = await provider.verifyPhoneNumber(phoneNumber, webVerifier);
  return { verificationId };
}

export async function confirmPhoneCode(session: PhoneSession, code: string): Promise<void> {
  const cred = PhoneAuthProvider.credential(session.verificationId, code);
  const user = auth.currentUser;
  if (user && user.isAnonymous) {
    await linkWithCredential(user, cred);
    track("account_linked", { method: "phone" });
  } else {
    await signInWithCredential(auth, cred);
    track("login_completed", { method: "phone" });
  }
}

// ---------------------------------------------------------------------------
// Mode invité
// ---------------------------------------------------------------------------
export async function signInGuest(): Promise<void> {
  await signInAnonymously(auth);
  track("login_completed", { method: "anonymous" });
}

/** Fournisseurs liés au compte courant (ids Firebase : google.com, apple.com, password, phone...). */
export function linkedProviderIds(user: User | null): string[] {
  if (!user) return [];
  return user.providerData.map((p) => p.providerId);
}

// ---------------------------------------------------------------------------
// Ré-authentification + suppression de compte (nLPD : droit à l'effacement)
// ---------------------------------------------------------------------------
export async function reauthenticate(password?: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  const ids = linkedProviderIds(user);
  if (ids.includes("password") && password && user.email) {
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    return;
  }
  const oauth = (["google.com", "apple.com", "microsoft.com", "facebook.com"] as const).find((p) => ids.includes(p));
  if (oauth) {
    const id = oauth.replace(".com", "") as OAuthProviderId;
    if (isNative()) await reauthenticateWithCredential(user, await nativeCredential(id));
    else await reauthenticateWithPopup(user, webProvider(id));
    return;
  }
  // Invité / Discord / téléphone / lien : pas de ré-auth possible ici, on laisse le serveur trancher.
}

/**
 * Supprime le compte : données RTDB (client) + auth + données serveur (proxy).
 * Ordre : serveur (idempotent) puis, en secours, suppression client.
 */
export async function deleteAccount(password?: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("not_signed_in");
  await reauthenticate(password);
  try {
    await deleteAccountOnServer();
  } catch (e) {
    // Serveur indisponible : suppression côté client (données + auth)
    await remove(ref(db, `users/${user.uid}`));
    await deleteUser(user);
  }
  track("account_deleted");
  try {
    localStorage.removeItem("st_profile_cache");
  } catch (e) {
    /* ignore */
  }
}
